import '../App.css';
import { ThemeProvider } from '@mui/material/styles';
import { Box, useMediaQuery, Typography, Backdrop, CircularProgress, Stack, Snackbar } from '@mui/material';
import { appTheme as theme } from '../theme';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { TableComponent } from './TableComponent';
import { API } from '../apiProperties';
import { HeaderComponent } from './HeaderComponent';
import { ReportComponent } from './ReportsComponent';
import { AppDistributionDashboard } from './AppDistributionDashboard';
import BuildAnalyticsDashboard from './BuildAnalyticsDashboard';
import { TutorialComponent } from './TutorialComponent';
import { HomeOverviewComponent } from './HomeOverviewComponent';
import { ErrorLogsComponent } from './ErrorLogsComponent';
import { AuditLogComponent } from './AuditLogComponent';
import { BuildsListComponent } from './BuildsListComponent';
import { ConfigReadinessComponent } from './ConfigReadinessComponent';
import { AppDetailsPage } from './AppDetailsPage';
import { BuildCostDashboard } from './BuildCostDashboard';
import { DeploySchedulerComponent } from './DeploySchedulerComponent';
import { ReleaseNotesComponent } from './ReleaseNotesComponent';
import { CommandPalette, useCommandPalette } from './CommandPalette';
import { useScheduledDeployRunner } from '../utils/scheduledDeploys';
import { useTheme, Theme } from '@mui/material/styles';
import { useAuth } from './AuthContext';
import { OnboardingTour } from './OnboardingTour';
import { fetchOnboardingCompleted, REPLAY_ONBOARDING_EVENT } from '../utils/onboarding';


const APP_BAR_HEIGHT = 68;

const versionStyles = `
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.9;
    }
  }
`;

export const HomeComponent: React.FC = () => {
  const stheme = useTheme<Theme>();
  const isMobile = useMediaQuery(stheme.breakpoints.down('md')); // Detect mobile view
  const { isLoadingAppData, dashboardData, fetchDashboardData, userInfo } = useAuth();
  const location = useLocation();
  const palette = useCommandPalette();
  // Scheduled deploys fire from the app shell, not from the Deploy Scheduler
  // page, so a schedule the operator queues and then navigates away from
  // still goes off at the scheduled time. Was previously gated behind that
  // page being mounted — operators on Home / App Deployment / Build Info
  // would silently miss their own scheduled deploys.
  useScheduledDeployRunner();

  // Bridge from CommandPalette's "Refresh dashboard data" action to the auth
  // context's fetcher — the palette dispatches a CustomEvent so it doesn't
  // need to know about useAuth internals.
  useEffect(() => {
    const onRefresh = () => { fetchDashboardData?.(); };
    const onOpenPalette = () => palette.openPalette();
    const onReplayOnboarding = () => setOnboardingOpen(true);
    window.addEventListener('fsam:refresh-dashboard', onRefresh as EventListener);
    window.addEventListener('fsam:open-palette', onOpenPalette as EventListener);
    window.addEventListener(REPLAY_ONBOARDING_EVENT, onReplayOnboarding as EventListener);
    return () => {
      window.removeEventListener('fsam:refresh-dashboard', onRefresh as EventListener);
      window.removeEventListener('fsam:open-palette', onOpenPalette as EventListener);
      window.removeEventListener(REPLAY_ONBOARDING_EVENT, onReplayOnboarding as EventListener);
    };
  }, [fetchDashboardData, palette]);

  // --- FIRST-LOGIN ONBOARDING ---
  // Once we know who the user is, ask the backend whether they've completed the
  // walkthrough. If not, open it. The guard ref makes this fire at most once
  // per mount so re-renders / dashboard refreshes can't reopen it.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const onboardingCheckedRef = useRef(false);

  useEffect(() => {
    if (!userInfo || onboardingCheckedRef.current) return;
    onboardingCheckedRef.current = true;
    fetchOnboardingCompleted().then((completed) => {
      if (!completed) setOnboardingOpen(true);
    });
  }, [userInfo]);

  // Close the tour. Completion is persisted inside OnboardingTour (on finish or
  // skip) so the walkthrough only auto-shows once; here we just flip it off.
  const handleOnboardingClose = useCallback((_didFinish: boolean) => {
    setOnboardingOpen(false);
  }, []);

  // FIX: Track if the initial load has completed to prevent the blocker from appearing on background refreshes
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (!isLoadingAppData) {
      setHasLoadedOnce(true);
    }
  }, [isLoadingAppData]);

  // --- USER ACTIVITY TRACKING ---
  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();
      const lastSent = parseInt(sessionStorage.getItem('lastActivitySent') || '0', 10);
      if (now - lastSent > 30000) {
        // Auth is the OIDC session cookie (sent automatically same-origin); the
        // old "Bearer <token>" header was always "Bearer null" here.
        fetch(API.update_activity, { method: 'POST', credentials: 'include' }).catch(() => {});
        sessionStorage.setItem('lastActivitySent', now.toString());
      }
    };
    window.addEventListener('click', handleActivity);
    return () => window.removeEventListener('click', handleActivity);
  }, []);

  // --- IDLE MODE SNACKBAR ---
  const [idleSnackOpen, setIdleSnackOpen] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem('sessionStartTime')) {
      sessionStorage.setItem('sessionStartTime', Date.now().toString());
    }
    const sessionAge = Date.now() - parseInt(sessionStorage.getItem('sessionStartTime') || Date.now().toString(), 10);
    const isNewSession = sessionAge < 60000;
    if (dashboardData?.isSessionIdle && !isNewSession) {
      setIdleSnackOpen(true);
    } else {
      setIdleSnackOpen(false);
    }
  }, [dashboardData]);

  return (
    <ThemeProvider theme={theme}>
      <style>{versionStyles}</style>

      <Box sx={{ width: '100vw', minHeight: '100vh', overflowX: 'hidden', p: 0, backgroundColor: 'background.default' }}>
        <HeaderComponent />
        {/* Top-nav layout — content takes the full width below the header. */}
        <Box
          sx={{
            marginTop: `${APP_BAR_HEIGHT}px`,
            minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
            width: '100%',
            position: 'relative',
            backgroundColor: 'background.default',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden',
              backgroundColor: 'background.default',
              position: 'relative',
            }}
          >
            <Box sx={{
              p: isMobile ? 1.5 : 3,
              position: 'relative',
              zIndex: 1,
              maxWidth: 1600,
              mx: 'auto',
              width: '100%',
            }}>
              {/* App Deployment Page */}
              <Box
                data-tour="page-deployment"
                style={{
                  display: location.pathname === '/home/deployment' ? 'flex' : 'none',
                  flex: 1,
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <TableComponent
                  url1={API.trigger}
                  isMobile={isMobile}
                  isActive={location.pathname === '/home/deployment'}
                />
              </Box>

              {/* Analytics Page */}
              <Box
                data-tour="page-app-distribution"
                style={{
                  display: location.pathname === '/home/app-distribution' ? 'block' : 'none',
                }}
              >
                <AppDistributionDashboard isActive={location.pathname === '/home/app-distribution'} />
              </Box>

              {/* Dashboard Home Page */}
              <div
                data-tour="page-home"
                style={{
                  display: location.pathname === '/home' || location.pathname === '/' ? 'block' : 'none',
                }}
              >
                {/* Personalized greeting + day summary + watch list */}
                <HomeOverviewComponent />

                {/* Reports Component (Recent Activity Feed) */}
                <Box sx={{ mt: 4 }}>
                  <ReportComponent isActive={location.pathname === '/home' || location.pathname === '/'} />
                </Box>
              </div>

              {/* Error Logs Page */}
              <div
                data-tour="page-build-analytics"
                style={{
                  display: location.pathname === '/home/build-analytics' ? 'block' : 'none',
                }}
              >
                <BuildAnalyticsDashboard
                  isActive={location.pathname === '/home/build-analytics'}
                />
              </div>

              {/* Build Browser Page */}
              <div
                data-tour="page-builds"
                style={{
                  display: location.pathname === '/home/builds' ? 'block' : 'none',
                }}
              >
                <BuildsListComponent isActive={location.pathname === '/home/builds'} />
              </div>

              {/* Config Readiness Page */}
              <div
                data-tour="page-apps"
                style={{
                  display: location.pathname === '/home/apps' ? 'block' : 'none',
                }}
              >
                <ConfigReadinessComponent isActive={location.pathname === '/home/apps'} />
              </div>

              {/* App Details Page (reached via "view app details" links) */}
              <div
                style={{
                  display: location.pathname === '/home/app-details' ? 'block' : 'none',
                }}
              >
                <AppDetailsPage isActive={location.pathname === '/home/app-details'} />
              </div>

              {/* Build Duration Dashboard */}
              <div
                data-tour="page-build-cost"
                style={{
                  display: location.pathname === '/home/build-cost' ? 'block' : 'none',
                }}
              >
                <BuildCostDashboard isActive={location.pathname === '/home/build-cost'} />
              </div>

              {/* Deploy Scheduler */}
              <div
                data-tour="page-deploy-scheduler"
                style={{
                  display: location.pathname === '/home/deploy-scheduler' ? 'block' : 'none',
                }}
              >
                <DeploySchedulerComponent isActive={location.pathname === '/home/deploy-scheduler'} />
              </div>

              {/* Release Notes editor */}
              <div
                data-tour="page-release-notes"
                style={{
                  display: location.pathname === '/home/release-notes' ? 'block' : 'none',
                }}
              >
                <ReleaseNotesComponent isActive={location.pathname === '/home/release-notes'} />
              </div>

              {/* Error Logs Page */}
              <div
                data-tour="page-error-logs"
                style={{
                  display: location.pathname === '/home/error-logs' ? 'block' : 'none',
                }}
              >
                <ErrorLogsComponent isActive={location.pathname === '/home/error-logs'} />
              </div>

              {/* Audit Log Page */}
              <div
                data-tour="page-audit"
                style={{
                  display: location.pathname === '/home/audit' ? 'block' : 'none',
                }}
              >
                <AuditLogComponent isActive={location.pathname === '/home/audit'} />
              </div>

              {/* Help & Tutorial Page */}
              <div
                style={{
                  display: location.pathname === '/home/tutorial' ? 'block' : 'none',
                }}
              >
                <TutorialComponent isActive={location.pathname === '/home/tutorial'} />
              </div>
            </Box>

            {/* Loading Backdrop - FIX: Only show on initial load (!hasLoadedOnce) */}
            <Backdrop
              sx={{
                color: '#fff',
                zIndex: (thems) => thems.zIndex.drawer + 999,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                background: 'rgba(0, 0, 0, 0.8)'
              }}
              // Only open if it is loading AND we haven't completed the first load yet
              open={isLoadingAppData && !hasLoadedOnce}
            >
              <CircularProgress color="inherit" size={60} />
              <Stack alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Refreshing App Data
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.8 }}>
                  Synchronizing latest build status...
                </Typography>
              </Stack>
            </Backdrop>
          </Box>
        </Box>

        {/* Idle Mode Snackbar */}
        <Snackbar
          open={idleSnackOpen}
          autoHideDuration={6000}
          onClose={() => setIdleSnackOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message="System is in idle mode. Data may be delayed."
          action={
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => { setIdleSnackOpen(false); window.location.reload(); }}
                style={{
                  background: 'white',
                  color: '#333',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                REFRESH
              </button>
            </div>
          }
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: '#ff9800', // Warning/Orange color
              color: 'white',
              fontWeight: 500
            }
          }}
        />

      </Box>

      <CommandPalette open={palette.open} onClose={palette.closePalette} />

      <OnboardingTour active={onboardingOpen} onClose={handleOnboardingClose} />
    </ThemeProvider>
  );
};